import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import {
  AdminPanelSettings as PlatformIcon,
  Logout as ExitIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const ADMIN_PORTAL_URL = process.env.REACT_APP_ADMIN_PORTAL_URL || 'http://localhost:3002';

/**
 * Shown across the top of every screen while a platform admin is working inside a
 * customer's organisation (see `pae` in the session token). Two modes:
 *
 *  - inside a clinic, as its administrator — actions are recorded against the
 *    platform admin;
 *  - acting AS a named user — the session runs under THAT person's identity, so
 *    anything written is attributed to them in the record.
 *
 * The second case is the dangerous one and gets the stronger treatment: red, and it
 * names whose account you are in. The banner is deliberately loud and never
 * dismissible — signing a note under a clinician's name by accident is exactly the
 * mistake this exists to prevent.
 */
export default function PlatformEntryBanner() {
  const { user, isPlatformEntry, exitPlatformEntry } = useAuth();
  if (!isPlatformEntry) return null;

  const clinicName = user?.practice_name || user?.node_name || 'this clinic';
  // Entered at a level rather than an office, the banner names the level.
  const where = { enterprise: 'enterprise', organization: 'organisation', region: 'region' }[
    user?.node_scope] || 'clinic';
  const operator = user?.acting_as?.email || user?.email;
  const impersonating = !!user?.acting_as?.impersonating_user;
  const accent = impersonating ? '#b91c1c' : '#7c3aed';
  const text = impersonating ? '#7f1d1d' : '#5b21b6';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        mb: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: accent,
        bgcolor: impersonating ? 'rgba(185, 28, 28, 0.08)' : 'rgba(124, 58, 237, 0.08)',
      }}
    >
      <PlatformIcon sx={{ color: accent }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ color: text, fontWeight: 700, lineHeight: 1.2 }}>
          {impersonating
            ? `You are signed in as ${user?.full_name || user?.email} at ${clinicName}`
            : `You are inside ${clinicName} as a platform admin`}
        </Typography>
        <Typography variant="caption" sx={{ color: text }}>
          {impersonating
            ? `This is not your account. Anything you create — including clinical notes — is
               recorded under their name. Your access was opened by ${operator}.`
            : `Anything you change here affects this ${where}'s live data, and is
               recorded against ${operator}.`}
        </Typography>
      </Box>
      <Chip
        label={impersonating ? 'Acting as user' : 'Platform access'}
        size="small"
        sx={{ bgcolor: accent, color: '#fff', fontWeight: 700 }}
      />
      <Button
        size="small"
        variant="outlined"
        startIcon={<ExitIcon fontSize="small" />}
        onClick={() => exitPlatformEntry(ADMIN_PORTAL_URL)}
        sx={{ color: text, borderColor: accent, whiteSpace: 'nowrap' }}
      >
        {impersonating ? 'Exit account' : `Exit ${where}`}
      </Button>
    </Box>
  );
}
